import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from "react-bootstrap"
import { Button, IconButton, Dialog, DialogContent } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CloseIcon from '@material-ui/icons/Close';
import SvgIcon from '../../../Helpers/Svg/SvgIcon';
import InfoIcon from '@material-ui/icons/Info';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import NumberFormat from 'react-number-format';
import { makeStyles, withStyles } from "@material-ui/core/styles";

import './QuoteCard.scss';
import { QuoteProductID } from '../QuoteProductID';

const TooltipColor = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  textPrimary: {
    color: theme.palette.primary.main
  },
  quoteCardColor: {
    color: theme.palette.primary.main,
  },
}));

export default function QuoteCard(props) {
  const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);
  const classes = useStyles();
  const isHavenLifeSIProduct = props.quote && props.quote.productID === QuoteProductID.HAVEN_SI_PRODUCT_ID;
  const isHavenTermProduct = props.quote && props.quote.productID === QuoteProductID.HAVEN_TERM_PRODUCT_ID;

  const productDetailsButton = (productName, isMobile) => {
    return (
      <Row>
        <Col>
          <Button className={`small-text ${isMobile ? "mobile-details-button" : ""}`} color="primary" onClick={() => setIsProductDetailsDialogOpen(true)} aria-describedby={productName}>{!isMobile && `PRODUCT`} DETAILS</Button>
        </Col>
      </Row>
    );
  }

  return (
    <div className="quote-card">
      <Row>
        <Col sm={4} className="d-flex align-items-start flex-column">
          <div className="d-flex flex-column w-100">
            <img className="carrier-logo-lg" src={props.logoUrl} alt={`${props.carrierName} Logo`} />
          </div>
        </Col>
        <Col>
          <h4>{props.productName} {isHavenLifeSIProduct ? props.quote.termLength : ''}</h4>
          {props.productNameSubtitle && <h5 className={classes.textPrimary}>{props.productNameSubtitle}</h5>}
          <div className={`quote-underwriting-time ${props.underwritingTime ? '' : 'd-none'}`}>Typically <span>{props.underwritingTime}</span></div>
        </Col>
        <Col xs="auto">
          <p>
            <NumberFormat
              className="quote-amount-dollars"
              value={props.monthlyRate}
              displayType={'text'}
              thousandSeparator={true}
              decimalScale={2}
              fixedDecimalScale={true}
            />
            <span className="quote-amount-mo"> /mo</span>
          </p>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex">
          <div className="quote-rating-section mt-auto">
            Rating &nbsp;<span className={`carrier-rating ${classes.textPrimary}`}>{props.rating}</span>
          </div>
        </Col>
        <Col xs={12} sm={3} className="d-flex justify-content-center">
          <Button
            className="w-100 apply"
            color="primary"
            variant="contained"
            onClick={props.onClick}
          >
            {props.buttonText || "Select"}
          </Button>
        </Col>
      </Row>
      <Row className={`quote-details-section`}>
        <Col sm={{ span: "auto", offset: 4 }} className="d-flex justify-content-center">
          {productDetailsButton(props.productName)}
        </Col>
      </Row>
      <ProductDetailsDialog
        isOpen={isProductDetailsDialogOpen}
        productDetails={props.productDetails}
        quoteCardProps={props}
        onClose={() => setIsProductDetailsDialogOpen(false)}
        isHavenTermProduct={isHavenTermProduct}
        isHavenLifeSIProduct={isHavenLifeSIProduct}
      />
    </div>
  )
}

const ProductDetailsRatingCard = (props) => {
  const classes = useStyles();
  return (
    <Card className="rating-card">
      <div className="rating-card-head">
        <img src={props.logo} alt={props.rater} className="img-fluid" />
      </div>
      <p className="rater">{props.rater}</p>
      <p className={`rating ${classes.quoteCardColor}`}>{props.rating}</p>
    </Card>
  );
};

// Product Details Dialog
export const ProductDetailsDialog = (props) => {

  const [tooltip, setTooltip] = useState(false);
  const productTermLength = props.quoteCardProps.quote.termLength;

  const handleTooltipClose = () => {
    setTooltip(false);
  };

  const handleTooltipOpen = () => {
    setTooltip(true);
  };
  const classes = useStyles();
  const hasRating =
    props.quoteCardProps.quote.ratingAMBest ||
    props.quoteCardProps.quote.ratingSP ||
    props.quoteCardProps.quote.ratingMoodys ||
    props.quoteCardProps.quote.ratingFitch ||
    props.quoteCardProps.quote.ratingComdex;

  return (
    <Dialog className="product-details-dialog" open={props.isOpen} scroll="body" fullWidth={true} maxWidth="md" aria-labelledby="product-details-dialog">
      <DialogContent className="product-details-dialog-content">
        <IconButton aria-label="close" onClick={props.onClose} className="close-button">
          <CloseIcon />
        </IconButton>
        <img className="carrier-logo-lg" src={props.quoteCardProps.quote.logoUrl} alt={`${props.quoteCardProps.quote.carrierName}`} />

        <p className={`mt-3 textRating ${classes.quoteCardColor}`}>
          {props.quoteCardProps.quote.rating}
          <span className="ml-1">Rating</span>
        </p>

        <div className="product mb-1">
          <span>{props.quoteCardProps.quote.productName}</span>
          <span> {props.isHavenLifeSIProduct ? productTermLength : `- ${productTermLength} Year Term`}</span>
        </div>

        <Row>
          <Col xs="3" md="9">
            <Stats
              underwritingTimeTypical={props.quoteCardProps.quote.underwritingTimeTypical}
              additionalOptionsStars={props.quoteCardProps.additionalOptionsStars}
              requiresMedicalExam={props.quoteCardProps.quote.requiresMedicalExam}
              monthlyRate={props.quoteCardProps.quote.monthlyRate}
              rating={props.quoteCardProps.quote.rating}
            />
          </Col>

          <Col xs="6" md="3">
            {!props.hideSelect && <Button className={`w-100 apply ${props.quoteCardProps.hideSelect ? "d-none" : ""}`} color="primary" variant="contained" onClick={props.quoteCardProps.onClick}>
              Select
            </Button>}
          </Col>
        </Row>

        <div className="product-details-info">{props.productDetails}</div>

        <div className="additional-options mt-4">
          <strong>
            <h2><b>Additional Options</b></h2>
          </strong>
          <Row>
            <Col md="3" className="mt-2 ml-2">
              <p className="option-title ">
                <b>Convertibility</b>
              </p>
              {props.quoteCardProps.quote.hasConvertibility ? (
                <p className="price-free mt-2">
                  <SvgIcon className="mr-1" iconName="select" />
                  <b>This product is convertible.</b>
                </p>
              ) : (
                <p className="price-red mt-2">
                  <SvgIcon className="mr-1" iconName="notConvertible" />
                  <b>This product is not convertible.</b>
                </p>
              )}
            </Col>
            {props.quoteCardProps.quote.hasConvertibility && (
              <Col className="mt-2 ml-2">
                <ul className="grey-text" dangerouslySetInnerHTML={{ __html: props.quoteCardProps.quote.convertibility }}></ul>
              </Col>
            )}
          </Row>
          {(props.quoteCardProps.quote.availableRiders && props.quoteCardProps.quote.availableRiders.length > 0) && (
            <strong className="mt-4 ml-2">
              <b>Available Policy Riders</b>
              <Tooltip className="toolInfo ml-2" title="Riders add benefits to or change the term of an insurance policy.">
                <TooltipColor role="tooltip" onClose={handleTooltipClose} open={tooltip} leaveTouchDelay={5000}>
                  <button id="riders" role="tooltip" aria-label="more about riders" className="quote-tooltip" onMouseOver={handleTooltipOpen} onClick={handleTooltipOpen} tabIndex="0" aria-disabled="true" aria-describedby="riders">
                    <InfoIcon color="action"></InfoIcon>
                  </button>
                </TooltipColor>
              </Tooltip>
            </strong>
          )}

          {props.quoteCardProps.quote.availableRiders &&
            props.quoteCardProps.quote.availableRiders.length > 0 &&
            props.quoteCardProps.quote.availableRiders.map((r, index) => {
              return (
                <Row key={index}>
                  <Col md="3">
                    <p className="option-title mt-4 ml-3">{r.rider}</p>
                    {r.additionalCost && (
                      <p className="price-extra mt-2 ml-3">
                        <MonetizationOnOutlinedIcon /> <b className="addtional">Additional Cost</b>
                      </p>
                    )}
                    {!r.additionalCost && (
                      <p className="mt-2 ml-3 moneyIcon">
                        <MonetizationOnOutlinedIcon /> <b className={`noAddtional ${classes.quoteCardColor}`}>No Additional Cost</b>
                      </p>
                    )}
                  </Col>
                  <Col>
                    <p dangerouslySetInnerHTML={{ __html: r.description }} className="grey-text mt-4 ml-2"></p>
                  </Col>
                </Row>
              );
            })}
        </div>

        {hasRating && (
          <div className="ratings">
            <div>
              <h2><strong>Insurance Carrier Ratings</strong></h2>
            </div>
            <ul className="d-flex rating-cards">
              <li>
                {props.quoteCardProps.quote.ratingAMBest && (
                  <ProductDetailsRatingCard logo="/logos/ratings/ambest.jpg" rater="AM Best" rating={props.quoteCardProps.quote.ratingAMBest} />
                )}
              </li>
              <li>
                {props.quoteCardProps.quote.ratingSP && <ProductDetailsRatingCard logo="/logos/ratings/sp.jpg" rater="S&amp;P Global" rating={props.quoteCardProps.quote.ratingSP} />}
              </li>
              <li>
                {props.quoteCardProps.quote.ratingMoodys && (
                  <ProductDetailsRatingCard logo="/logos/ratings/moodys.jpg" rater="Moody's" rating={props.quoteCardProps.quote.ratingMoodys} />
                )}
              </li>
              <li>
                {props.quoteCardProps.quote.ratingFitch && <ProductDetailsRatingCard logo="/logos/ratings/fitch.jpg" rater="Fitch" rating={props.quoteCardProps.quote.ratingFitch} />}
              </li>
              <li>
                {props.quoteCardProps.quote.ratingComdex && (
                  <ProductDetailsRatingCard logo="/logos/ratings/comdex.jpg" rater="Comdex" rating={props.quoteCardProps.quote.ratingComdex} />
                )}
              </li>
            </ul>
          </div>
        )}
        {props.isHavenTermProduct || props.isHavenLifeSIProduct ? (
          <div className="mt-4">
            <h3 className=" text-center">Disclosure</h3>
            <p className="grey-text">{props.quoteCardProps.quote.disclosureDetail}</p>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const Stats = (props) => {
  return (
    <div className="product-details-stats">
      <Row>
        <Col className="d-flex flex-column justify-content-center">
          <p className="stat-title">Approval Time</p>
          <p className="stat-text">{props.underwritingTimeTypical}</p>
        </Col>

        <Col className="d-flex flex-column justify-content-center">
          <p className="stat-title">Medical Exam</p>
          <p className="stat-text">{props.requiresMedicalExam}</p>
        </Col>
      </Row>
    </div>
  );
};

QuoteCard.propTypes = {
  carrierName: PropTypes.string.isRequired,
  monthlyRate: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  expand: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
  buttonText: PropTypes.string,
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/Cards/QuoteCard.js